import { forwardRef, useEffect, useMemo, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getPaymentTermsOptions } from '@/modules/clients/constants/payment-terms';
import { ClientInfoFormData, getClientInfoSchema } from '@/modules/clients/validations';
import { SingleSelect } from '@/modules/common';
import { MultiEmailInput } from '@/modules/common/components/molecules/inputs/multi-email-input';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { FileUploadField } from '@/modules/common/components/organisms/file-upload-field';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  SAVE_FLOW_CONTROL_ERRORS,
  useTabFormState,
} from '@/modules/common/components/templates/feature-details';
import { useTeamMemberSelectors } from '@/modules/team';
import { compareFormValues } from '@/utils/form-state.utils';

import { QuickBooksSyncedLabel } from '../../../components/quickbooks-synced-label';
import { Client, ClientFormData, ClientStatus, PaymentTerms } from '../../../types';
import { findEditedFields } from '../../../utils/find-edited-fields';
import {
  findChangedQuickBooksSyncedFields,
  mapClientToQuickBooksSyncedValues,
  QuickBooksSyncedField,
} from '../../../utils/quickbooks-synced-fields';

export interface ClientInfoTabProps extends BaseTabProps<Client, ClientFormData> {
  onQuickBooksConfirmationRequired?: (changedFields: QuickBooksSyncedField[]) => void;
  /** Create mode with QuickBooks connected: the client is created there too, so its limits apply */
  isCreatedInQuickBooks?: boolean;
}

export interface ClientInfoTabRef extends FeatureTabRef<ClientFormData> {}

export const ClientInfoTab = forwardRef<ClientInfoTabRef, ClientInfoTabProps>((props, ref) => {
  const { t } = useTranslation(['authenticated.clients', 'common']);
  const {
    mode,
    featureData: clientData,
    draftData,
    onQuickBooksConfirmationRequired,
    isCreatedInQuickBooks = false,
  } = props;
  const isQuickBooksLinked = mode !== FeatureMode.Create && Boolean(clientData?.isQuickBooksLinked);
  const schema = getClientInfoSchema(t, {
    isQuickBooksLinked: isQuickBooksLinked || isCreatedInQuickBooks,
  });

  const { loadManagers, searchManagers } = useTeamMemberSelectors();

  const initialValues = useMemo((): ClientInfoFormData => {
    if (mode === FeatureMode.Create) {
      return {
        name: draftData?.name ?? '',
        email: draftData?.email ?? '',
        managerId: draftData?.managerId ?? '',
        paymentTerms: draftData?.paymentTerms ?? PaymentTerms.Net30,
        billingAddress: draftData?.billingAddress ?? '',
        shippingAddress: draftData?.shippingAddress ?? '',
      };
    }

    const syncedValues = mapClientToQuickBooksSyncedValues(clientData);
    return {
      name: draftData?.name ?? syncedValues.name,
      email: draftData?.email ?? syncedValues.email,
      managerId: draftData?.managerId ?? clientData?.manager?.id ?? '',
      paymentTerms: draftData?.paymentTerms ?? syncedValues.paymentTerms,
      billingAddress: draftData?.billingAddress ?? syncedValues.billingAddress,
      shippingAddress: draftData?.shippingAddress ?? syncedValues.shippingAddress,
    };
  }, [mode, clientData, draftData]);

  const form = useForm<ClientInfoFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: initialValues,
  });

  const getFormData = (): ClientFormData => {
    const formValues = form.getValues();

    return {
      name: formValues.name,
      status: clientData?.status || ClientStatus.Active,
      email: formValues.email,
      managerId: formValues.managerId,
      paymentTerms: formValues.paymentTerms,
      billingAddress: formValues.billingAddress,
      shippingAddress: formValues.shippingAddress,
      avatar: formValues.avatar,
    } as ClientFormData;
  };

  // Values saved elsewhere (QuickBooks, another user) reach the open form; the user's edits stay
  const previousInitialValuesRef = useRef(initialValues);
  useEffect(() => {
    const previousInitialValues = previousInitialValuesRef.current;
    previousInitialValuesRef.current = initialValues;
    if (mode === FeatureMode.Create || previousInitialValues === initialValues) return;

    const currentValues = form.getValues();
    const editedFields = findEditedFields(currentValues, previousInitialValues);
    form.reset(initialValues);
    editedFields.forEach(field => {
      form.setValue(field, currentValues[field], { shouldDirty: true, shouldValidate: true });
    });
  }, [form, initialValues, mode]);

  // The address limit tightens once it is known the client is created in QuickBooks too
  useEffect(() => {
    if (mode === FeatureMode.Create) void form.trigger(['billingAddress', 'shippingAddress']);
  }, [form, mode, isCreatedInQuickBooks]);

  // Use the tab form state hook with adapter
  const { isReadMode, handleSubmit } = useTabFormState<Client, ClientInfoFormData>(props, {
    form,
    initialValues,
    isArchivedFn: client => client?.status === ClientStatus.Archived,
    triggerValidationOnMount: true,
    customDirtyStateFn: (currentValues, initialValues) => {
      if (!form.formState.isValid) {
        return false;
      }

      // Check if there are actual changes
      if (initialValues) {
        return compareFormValues(currentValues, initialValues);
      }

      // Fallback to form's built-in dirty state
      return form.formState.isDirty;
    },
  });

  const handleSubmitWithQuickBooksConfirmation = async (): Promise<void> => {
    await handleSubmit();

    const changedSyncedFields = isQuickBooksLinked
      ? findChangedQuickBooksSyncedFields(form.getValues(), initialValues)
      : [];
    if (changedSyncedFields.length === 0 || !onQuickBooksConfirmationRequired) return;

    onQuickBooksConfirmationRequired(changedSyncedFields);
    // The confirmation modal takes the save over
    throw new Error(SAVE_FLOW_CONTROL_ERRORS.ConflictResolutionRequired);
  };

  const shouldShowWarningForMemberId = (item: DropdownSearchItem) => {
    return item.isDeleted ? t('details.deletedEntityWanring') : undefined;
  };

  // Include deleted manager in dropdown options if exists
  const additionalManagerItems = useMemo(() => {
    if (clientData?.manager?.isDeleted) {
      return [
        {
          id: clientData.manager.id,
          name: clientData.manager.name,
          secondaryText: clientData.manager.email ?? undefined,
          avatarLink: clientData.manager.avatar ?? undefined,
          isDeleted: true,
        },
      ];
    }
    return undefined;
  }, [clientData?.manager]);

  // Create initial selected item for manager to display immediately without fetching
  const initialManagerItem = useMemo(() => {
    if (clientData?.manager) {
      return {
        id: clientData.manager.id,
        name: clientData.manager.name,
        secondaryText: clientData.manager.email ?? undefined,
        avatarLink: clientData.manager.avatar ?? undefined,
        isDeleted: clientData.manager.isDeleted,
      };
    }
    return undefined;
  }, [clientData?.manager]);

  const tabContent = (
    <Form {...form}>
      <form className="space-y-4">
        {(isQuickBooksLinked || isCreatedInQuickBooks) && (
          <Alert>
            <AlertDescription>
              {t(
                isCreatedInQuickBooks
                  ? 'details.quickBooksSync.createNote'
                  : 'details.quickBooksSync.note'
              )}
            </AlertDescription>
          </Alert>
        )}
        <Card>
          <CardHeader>
            <CardTitle>{t('details.tabs.info.mainInfoTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <QuickBooksSyncedLabel
                      label={t('details.tabs.info.fullName')}
                      isSynced={isQuickBooksLinked}
                    />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('details.tabs.info.namePlaceholder')}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <QuickBooksSyncedLabel
                      label={t('details.tabs.info.email')}
                      isSynced={isQuickBooksLinked}
                    />
                  </FormLabel>
                  <FormControl>
                    <MultiEmailInput
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('details.tabs.info.emailPlaceholder')}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {mode === FeatureMode.Create && (
              <FileUploadField
                name="avatar"
                control={form.control}
                label={t('details.tabs.info.avatar')}
                disabled={isReadMode}
              />
            )}
            <SingleSelectWithPagination
              name="managerId"
              initialSelectedItem={initialManagerItem}
              showWarning={shouldShowWarningForMemberId}
              additionalItems={additionalManagerItems}
              control={form.control}
              label={t('details.tabs.info.manager')}
              placeholder={
                isReadMode
                  ? t('details.tabs.info.notSelected')
                  : t('details.tabs.info.managerPlaceholder')
              }
              disabled={isReadMode}
              showAvatars={true}
              showDescriptionInOptions={true}
              showDescriptionInSelector={false}
              avatarShape="circle"
              queryKey={['team-members-short', 'managers']}
              loadItems={loadManagers}
              searchItems={searchManagers}
              isClearSelectionEnabled={true}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="mr-4">{t('details.tabs.info.mainBillingTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SingleSelect
              name="paymentTerms"
              control={form.control}
              options={getPaymentTermsOptions(t)}
              label={
                <QuickBooksSyncedLabel
                  label={t('details.tabs.info.paymentTerms')}
                  isSynced={isQuickBooksLinked}
                />
              }
              placeholder={
                isReadMode
                  ? t('details.tabs.info.notSelected')
                  : t('details.tabs.info.paymentTermsPlaceholder')
              }
              emptyMessage={t('common:loading')}
              disabled={isReadMode}
              searchable={false}
            />
            {isQuickBooksLinked && clientData?.unsupportedQuickBooksPaymentTerm && (
              <p className="text-sm text-warning">
                {t('details.quickBooksSync.unsupportedPaymentTerm', {
                  term: clientData.unsupportedQuickBooksPaymentTerm,
                })}
              </p>
            )}
            <FormField
              control={form.control}
              name="billingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <QuickBooksSyncedLabel
                      label={t('details.tabs.info.billingAddress')}
                      isSynced={isQuickBooksLinked}
                    />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder={t('details.tabs.info.billingAddressPlaceholder')}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="shippingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <QuickBooksSyncedLabel
                      label={t('details.tabs.info.shippingAddress')}
                      isSynced={isQuickBooksLinked}
                    />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder={t('details.tabs.info.shippingAddressPlaceholder')}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
      </form>
    </Form>
  );

  return (
    <BaseFeatureTab
      {...props}
      ref={ref}
      onSubmit={handleSubmitWithQuickBooksConfirmation}
      isValid={form.formState.isValid}
      getFormData={getFormData}
      isArchivedFn={data => data?.status === ClientStatus.Archived}
    >
      {tabContent}
    </BaseFeatureTab>
  );
});
